import AsyncStorage from '@react-native-async-storage/async-storage';
import messaging from '@react-native-firebase/messaging';
import { NavigationContainer, NavigationContainerRef } from '@react-navigation/native';
import { QueryClientProvider } from '@tanstack/react-query';
import * as Updates from 'expo-updates';
import i18n from 'i18next';
import React, { useEffect, useRef } from 'react';
import { AppState, I18nManager, Platform, StatusBar, View } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import ErrorBoundary from './src/components/ErrorBoundary';
import NetworkStatus from './src/components/NetworkStatus';
import './src/config/i18n';
import { AuthProvider } from './src/contexts/AuthContext';
import { PermissionProvider } from './src/contexts/PermissionContext';
import { ThemeProvider } from './src/contexts/ThemeContext';
import { queryClient } from './src/lib/queryClient';
import RootNavigator from './src/navigation/RootNavigator';
import { registerBackgroundAlarmCheck } from './src/services/backgroundTasks';
import notificationService, { registerBackgroundMessageHandler } from './src/services/notificationService';
import logger from './src/utils/logger';

// Register background message handler for FCM (must be at top level)
if (Platform.OS !== 'web') {
  try {
    registerBackgroundMessageHandler();

    // Initialize FCM notification channels
    notificationService.init().catch(error => {
      logger.error('Failed to initialize notification service:', error);
    });
  } catch (error) {
    logger.error('Fatal error initializing Firebase messaging:', error);
    // Continue app initialization even if FCM fails
  }
}

export default function App() {
  const appState = useRef(AppState.currentState);
  const navigationRef = useRef<NavigationContainerRef<any>>(null);

  // ✅ Silent OTA update check (non-blocking, background download)
  const checkOTAUpdatesInBackground = async () => {
    if (__DEV__ || Platform.OS === 'web' || !Updates.isEnabled) {
      return;
    }

    try {
      logger.log('🔍 [OTA] Checking for updates in background...');
      const update = await Updates.checkForUpdateAsync();

      if (update.isAvailable) {
        logger.log('📥 OTA update available, downloading silently...', {
          updateId: update.manifest?.id,
          timestamp: new Date().toISOString(),
        });

        await Updates.fetchUpdateAsync();

        logger.log('✅ OTA update downloaded, will apply on next app restart');
        logger.log('✅ [OTA] Update downloaded successfully');
      } else {
        logger.log('✅ [OTA] No updates available');
      }
    } catch (error) {
      logger.warn('⚠️ [OTA] Background update check failed:', error);
      logger.warn('Failed to check OTA updates in background', error);
    }
  };

  // Handle notification tap navigation
  const handleNotificationTap = (notificationData: any) => {
    logger.log('📱 Handling notification tap with data:', notificationData);

    // Wait for navigation to be ready
    setTimeout(() => {
      if (!navigationRef.current?.isReady()) {
        logger.warn('⚠️ Navigation not ready, retrying...');
        setTimeout(() => handleNotificationTap(notificationData), 500);
        return;
      }

      try {
        const notificationType = notificationData.type as string;

        switch (notificationType) {
          case 'alarm':
            // Navigate to Alarms screen
            logger.log('🚨 Navigating to Alarms screen');
            navigationRef.current?.navigate('Main', {
              screen: 'Alarms',
              params: {
                screen: 'AlarmsList',
                params: {
                  alarmId: notificationData.alarmId,
                  highlightAlarm: true,
                },
              },
            });
            break;

          case 'low_battery':
            // Navigate to Device detail screen
            if (notificationData.deviceId) {
              logger.log('🔋 Navigating to Device detail:', notificationData.deviceId);
              navigationRef.current?.navigate('Main', {
                screen: 'Devices',
                params: {
                  screen: 'DeviceDetail',
                  params: {
                    deviceId: notificationData.deviceId,
                  },
                },
              });
            } else {
              // Navigate to Devices list
              navigationRef.current?.navigate('Main', {
                screen: 'Devices',
              });
            }
            break;

          case 'device_offline':
            // Navigate to Device detail screen
            if (notificationData.deviceId) {
              logger.log('📵 Navigating to offline device:', notificationData.deviceId);
              navigationRef.current?.navigate('Main', {
                screen: 'Devices',
                params: {
                  screen: 'DeviceDetail',
                  params: {
                    deviceId: notificationData.deviceId,
                  },
                },
              });
            } else {
              // Navigate to Devices list
              navigationRef.current?.navigate('Main', {
                screen: 'Devices',
              });
            }
            break;

          case 'test':
          case 'general':
          default:
            // Navigate to Dashboard
            logger.log('🏠 Navigating to Dashboard');
            navigationRef.current?.navigate('Main', {
              screen: 'Dashboard',
            });
            break;
        }
      } catch (error) {
        logger.error('❌ Error navigating from notification:', error);
        // Fallback to Dashboard
        navigationRef.current?.navigate('Main', {
          screen: 'Dashboard',
        });
      }
    }, 1000); // Wait 1 second for app to be fully loaded
  };

  useEffect(() => {
    // Set RTL based on current language
    const isRTL = i18n.language === 'ar';
    if (I18nManager.isRTL !== isRTL) {
      I18nManager.forceRTL(isRTL);
      // Note: App needs to restart for RTL to take effect
      // This is handled in language change function
    }

    // ✅ OTA updates handled by SplashScreen on launch - no duplicate check needed here
    // Setup periodic OTA update check (every 6 hours - avoid excessive checks)
    const otaCheckInterval = setInterval(() => {
      logger.log('🔄 [OTA] Periodic update check (every 6h)');
      checkOTAUpdatesInBackground();
    }, 6 * 60 * 60 * 1000); // 6 hours

    // Register background alarm checking (skip on web)
    if (Platform.OS !== 'web') {
      registerBackgroundAlarmCheck().catch((error) => {
        logger.error('Failed to register background tasks:', error);
      });

      // Setup Notifee foreground tap handler
      notificationService.setupNotificationTapHandler((data: any) => {
        logger.log('👆 Notification tapped via Notifee (foreground):', data);
        handleNotificationTap(data);
      });

      // ✅ FIX: Resume navigation saved by notifee.onBackgroundEvent
      // (fires when notifee displayed a notification while app was in background)
      AsyncStorage.getItem('@pending_notification_nav')
        .then(pending => {
          if (pending) {
            AsyncStorage.removeItem('@pending_notification_nav').catch(() => { });
            const data = JSON.parse(pending);
            logger.log('🔔 Resuming pending notification navigation:', data);
            handleNotificationTap(data);
          }
        })
        .catch(err => logger.warn('Error reading pending notification nav:', err));

      // ✅ CRITICAL FIX: Handle notification tap when app in BACKGROUND
      const unsubscribeBackgroundTap = messaging().onNotificationOpenedApp(remoteMessage => {
        logger.log('👆 Notification tapped via FCM (background):', remoteMessage.data);
        if (remoteMessage.data) {
          handleNotificationTap(remoteMessage.data);
        }
      });

      // ✅ CRITICAL FIX: Handle notification when app opened from KILLED state
      messaging()
        .getInitialNotification()
        .then(remoteMessage => {
          if (remoteMessage) {
            logger.log('🚀 App opened from notification (killed state):', remoteMessage.data);
            if (remoteMessage.data) {
              handleNotificationTap(remoteMessage.data);
            }
          }
        })
        .catch(error => {
          logger.error('Error getting initial notification:', error);
        });

      // ✅ FIX: AppState listener inside non-web block so cleanup runs correctly
      const subscription = AppState.addEventListener('change', nextAppState => {
        if (
          appState.current.match(/inactive|background/) &&
          nextAppState === 'active'
        ) {
          logger.log('📱 App has come to foreground');
          notificationService.clearBadge();
        }
        appState.current = nextAppState;
      });

      return () => {
        unsubscribeBackgroundTap();
        clearInterval(otaCheckInterval);
        subscription.remove();
      };
    } else {
      return () => {
        clearInterval(otaCheckInterval);
      };
    }
  }, []);

  // Note: OTA Update checking is now handled in SplashScreen.tsx
  // which shows progress and handles automatic reload


  return (
    <QueryClientProvider client={queryClient}>
      <SafeAreaProvider>
        <ErrorBoundary>
          <ThemeProvider>
            <AuthProvider>
              <PermissionProvider>
                <NavigationContainer ref={navigationRef}>
                  {/* ✅ ANDROID 15 FIX: Use translucent + transparent for edge-to-edge */}
                  <StatusBar
                    barStyle="light-content"
                    backgroundColor="transparent"
                    translucent={true}
                  />
                  <View style={{ flex: 1 }}>
                    <NetworkStatus position="top" />
                    <RootNavigator />
                  </View>
                </NavigationContainer>
              </PermissionProvider>
            </AuthProvider>
          </ThemeProvider>
        </ErrorBoundary>
      </SafeAreaProvider>
    </QueryClientProvider>
  );
}
